import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Clock, Shield, Power, AlertTriangle, Eye, CheckCircle, 
  Search, Filter, ExternalLink, RefreshCw, X, Download,
  FileText, Lock, ChevronRight
} from 'lucide-react';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { getAuditEvents, getAuditEventDetail, exportAuditJSON } from '../services/api';
import { toast } from 'sonner';

const AuditPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [eventFilter, setEventFilter] = useState('all');
  const [drawerEvent, setDrawerEvent] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await getAuditEvents({ limit: 200 });
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch audit events:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events.filter((event) => {
    const matchesSearch = 
      event.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.agent_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.event_type?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = eventFilter === 'all' || event.event_type === eventFilter;
    return matchesSearch && matchesFilter;
  });

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'kill_switch_executed':
        return <Power className="w-4 h-4 text-[#FF3B30]" />;
      case 'violation_detected':
        return <AlertTriangle className="w-4 h-4 text-[#FFCC00]" />;
      case 'violation_resolved':
        return <CheckCircle className="w-4 h-4 text-[#34C759]" />;
      case 'discovery_completed':
      case 'discovery_scan_triggered':
        return <Eye className="w-4 h-4 text-[#0066FF]" />;
      case 'agent_reactivated':
        return <RefreshCw className="w-4 h-4 text-[#34C759]" />;
      default:
        return <Shield className="w-4 h-4 text-[#9CA3AF]" />;
    }
  };

  const getEventColor = (eventType) => {
    switch (eventType) {
      case 'kill_switch_executed':
        return 'border-[#FF3B30]';
      case 'violation_detected':
        return 'border-[#FFCC00]';
      case 'violation_resolved':
      case 'agent_reactivated':
        return 'border-[#34C759]';
      case 'discovery_completed':
      case 'discovery_scan_triggered':
        return 'border-[#0066FF]';
      default:
        return 'border-[#27272A]';
    }
  };

  const formatEventType = (type) => {
    return type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  const uniqueEventTypes = [...new Set(events.map(e => e.event_type))];

  const openDrawer = async (event) => {
    setDrawerLoading(true);
    setDrawerEvent(event);
    try {
      const resp = await getAuditEventDetail(event.id);
      setDrawerEvent(resp.data);
    } catch {
      // fallback to the event we already have
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const resp = await exportAuditJSON();
      const blob = new Blob([JSON.stringify(resp.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'audit-trail-export.json'; a.click();
      toast.success(`Exported ${resp.data.count} events`);
    } catch {
      toast.error('Failed to export audit trail');
    }
  };

  return (
    <div data-testid="audit-page">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#F9FAFB] mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Audit Timeline
          </h1>
          <p className="text-[#9CA3AF]">
            Complete audit trail of all security events, detections, and containment actions.
          </p>
        </div>
        <button
          onClick={handleExport}
          className="secondary-btn text-sm flex items-center gap-2"
          data-testid="export-audit-btn"
        >
          <Download className="w-4 h-4" /> Export JSON
        </button>
      </div>

      {/* Filters */}
      <div className="enterprise-card p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
            <Input
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none"
              data-testid="search-audit-events"
            />
          </div>
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="w-[200px] bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none" data-testid="event-type-filter">
              <SelectValue placeholder="Event Type" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
              <SelectItem value="all">All Events</SelectItem>
              {uniqueEventTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {formatEventType(type)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={fetchEvents} variant="outline" className="secondary-btn" data-testid="refresh-audit-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Timeline */}
      <div className="space-y-4">
        {loading ? (
          <div className="enterprise-card p-8 text-center text-[#9CA3AF]">Loading audit events...</div>
        ) : filteredEvents.length === 0 ? (
          <div className="enterprise-card p-8 text-center">
            <Clock className="w-12 h-12 text-[#6B7280] mx-auto mb-4" />
            <p className="text-[#9CA3AF]">No audit events found.</p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-px bg-[#27272A]" />
            
            {filteredEvents.map((event, index) => (
              <div 
                key={event.id} 
                className={`relative pl-16 pb-6 ${index === filteredEvents.length - 1 ? '' : ''}`}
                data-testid={`audit-event-${event.id}`}
              >
                {/* Timeline dot */}
                <div className={`absolute left-4 w-5 h-5 bg-[#121212] border-2 ${getEventColor(event.event_type)} flex items-center justify-center`}>
                  <div className="w-2 h-2 bg-current" />
                </div>
                
                {/* Event card */}
                <div
                  className={`enterprise-card p-4 border-l-4 ${getEventColor(event.event_type)} cursor-pointer hover:bg-[#1A1A1A]/50 transition-colors`}
                  onClick={() => openDrawer(event)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      {getEventIcon(event.event_type)}
                      <div>
                        <span className="text-[#F9FAFB] font-medium">
                          {formatEventType(event.event_type)}
                        </span>
                        {event.action && event.action !== event.event_type && (
                          <span className="text-[#6B7280] text-sm ml-2">
                            {event.action}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-[#6B7280] font-mono">
                      {new Date(event.timestamp).toLocaleString()}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm">
                    {event.agent_id && (
                      <Link 
                        to={`/dashboard/agents/${event.agent_id}`}
                        className="text-[#0066FF] hover:text-[#3385FF] flex items-center gap-1"
                      >
                        Agent: <span className="font-mono">{event.agent_id}</span>
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    )}
                    {event.user_name && (
                      <span className="text-[#9CA3AF]">
                        By: <span className="font-mono">{event.user_name}</span>
                      </span>
                    )}
                  </div>
                  
                  {event.details && Object.keys(event.details).length > 0 && (
                    <div className="mt-3 p-3 bg-[#1A1A1A] border border-[#27272A]/40">
                      <div className="text-xs text-[#6B7280] uppercase tracking-wider mb-2">Details</div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {Object.entries(event.details).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-[#6B7280]">{key}:</span>
                            <span className="font-mono text-[#9CA3AF]">
                              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {drawerEvent && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="audit-evidence-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDrawerEvent(null)} />
          <div className="relative w-full max-w-lg bg-[#121212] border-l border-[#27272A] overflow-y-auto">
            {/* Drawer Header */}
            <div className="sticky top-0 bg-[#121212] border-b border-[#27272A]/40 p-5 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-[#0066FF]" />
                <h2 className="text-base font-bold text-[#F9FAFB]">Audit Evidence</h2>
              </div>
              <button
                onClick={() => setDrawerEvent(null)}
                className="text-[#6B7280] hover:text-[#F9FAFB] transition-colors"
                data-testid="close-drawer-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {drawerLoading ? (
              <div className="p-8 text-center text-[#9CA3AF]">Loading evidence...</div>
            ) : (
              <div className="p-5 space-y-5">
                {/* Event Summary */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    {getEventIcon(drawerEvent.event_type)}
                    <span className="text-[#F9FAFB] font-medium">{formatEventType(drawerEvent.event_type)}</span>
                  </div>
                  <div className="text-sm text-[#9CA3AF]">{drawerEvent.action}</div>
                </div>

                {/* Metadata */}
                <div className="p-4 bg-[#0A0A0A] border border-[#27272A]/40 space-y-2">
                  <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-2">Event Metadata</div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6B7280]">Event ID</span>
                    <span className="font-mono text-[#F9FAFB] text-xs">{drawerEvent.id}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6B7280]">Timestamp</span>
                    <span className="font-mono text-[#9CA3AF] text-xs">{new Date(drawerEvent.timestamp).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6B7280]">Actor</span>
                    <span className="font-mono text-[#9CA3AF] text-xs">{drawerEvent.user_name || 'System'}</span>
                  </div>
                  {drawerEvent.agent_id && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[#6B7280]">Agent</span>
                      <Link to={`/dashboard/agents/${drawerEvent.agent_id}`} className="text-[#0066FF] font-mono text-xs hover:text-[#3385FF]">
                        {drawerEvent.agent_id}
                      </Link>
                    </div>
                  )}
                </div>

                {/* Agent Info (enriched) */}
                {drawerEvent.agent_info && (
                  <div className="p-4 bg-[#0A0A0A] border border-[#27272A]/40">
                    <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-2">Affected Agent</div>
                    <div className="space-y-1 text-sm">
                      {Object.entries(drawerEvent.agent_info).map(([k, v]) => (
                        <div key={k} className="flex justify-between">
                          <span className="text-[#6B7280]">{k}</span>
                          <span className="font-mono text-[#9CA3AF] text-xs">{String(v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Evidence Details */}
                {drawerEvent.details && Object.keys(drawerEvent.details).length > 0 && (
                  <div className="p-4 bg-[#0A0A0A] border border-[#0066FF]/20">
                    <div className="text-[10px] text-[#0066FF] uppercase tracking-wider mb-3">Evidence & Details</div>
                    <div className="space-y-2">
                      {Object.entries(drawerEvent.details).map(([key, value]) => (
                        <div key={key}>
                          <div className="text-[10px] text-[#6B7280] uppercase">{key.replace(/_/g, ' ')}</div>
                          {typeof value === 'object' ? (
                            <pre className="text-xs font-mono text-[#9CA3AF] bg-[#1A1A1A] p-2 mt-1 overflow-x-auto whitespace-pre-wrap border border-[#27272A]/40">
                              {JSON.stringify(value, null, 2)}
                            </pre>
                          ) : (
                            <div className="text-sm font-mono text-[#F9FAFB]">{String(value)}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Hash for compliance */}
                {drawerEvent.details?.evidence_hash && (
                  <div className="p-3 bg-[#34C759]/5 border border-[#34C759]/20">
                    <div className="flex items-center gap-2">
                      <Lock className="w-3.5 h-3.5 text-[#34C759]" />
                      <span className="text-[10px] text-[#34C759] uppercase">Tamper-proof evidence hash</span>
                    </div>
                    <div className="font-mono text-xs text-[#9CA3AF] mt-1">{drawerEvent.details.evidence_hash}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditPage;
